import { IssueFieldNames } from 'shared/domain/issueForm/types/fieldNames';
import { ReportFieldsContent } from '../../types/model/common';
import {
  isIssueFieldName,
  isReportFieldsContent,
} from '../../types/model/reportConfigGuards';
import { SingleInspectionReportOnView } from '../../types/onView/singleInspectionReportOnView';
import {
  deriveInspectionDataOptionsFromForm,
  deriveInspectionReportPartsFromForm,
  deriveInspectionTemplateDataFromForm,
  deriveLinkToHustroFromForm,
} from '../../mapping/inspectionReportApiMapping';
import {
  FileMetadataOptions,
  IssueAttachmentsEstimateConfig,
  SectionEstimateConfig,
} from '../buildReportPageEstimateInput';
import { readSectionEstimateConfigFromOnView } from '../sectionEstimateConfig';
import { InspectionPageEstimateSource } from './inspectionPageEstimateSource';

export type InspectionPartsEstimateConfig = {
  inspectionInfo: boolean;
  summary: boolean;
  checklist: boolean;
  issueRegister: boolean;
  signatures: boolean;
};

export type InspectionTemplateDataEstimateConfig = {
  generalStandard: boolean;
  generalStandardAttachments: SectionEstimateConfig;
  sectionDescriptions: boolean;
  sectionAttachments: SectionEstimateConfig;
  controlledElementStandard: boolean;
  controlledElementStandardAttachments: SectionEstimateConfig;
};

export type InspectionDataEstimateConfig = {
  controlledElementAttachments: SectionEstimateConfig;
  showNotApplicable: boolean;
  showUnchecked: boolean;
};

export type InspectionPageEstimateInput = {
  parts: InspectionPartsEstimateConfig;
  templateData: InspectionTemplateDataEstimateConfig;
  inspectionData: InspectionDataEstimateConfig;
  issueAttachments: IssueAttachmentsEstimateConfig;
  fieldsContent: ReportFieldsContent;
  selectedFieldNames: IssueFieldNames[];
  getAvailableFieldsForProcess: (processId: string) => Set<IssueFieldNames>;
  fileMetadata: FileMetadataOptions;
  showUserEmails: boolean;
  linkToHustro: boolean;
  /** The report name, printed as the title of the first page. */
  reportName: string;
  reportMessage: string;
  reportMessageInPdf: boolean;
  source: InspectionPageEstimateSource;
};

/** Stable key of form fields that affect the inspection page estimate. */
export function getInspectionPageEstimateFormDependencyKey(
  values: SingleInspectionReportOnView
): string {
  return JSON.stringify({
    templateSectionEnabled: values.templateSectionEnabled,
    checklistSectionEnabled: values.checklistSectionEnabled,
    issueDataSectionEnabled: values.issueDataSectionEnabled,
    inspectionInfo: values.inspectionInfo,
    summary: values.summary,
    signatures: values.signatures,
    generalStandard: values.generalStandard,
    generalStandardAttachments: readSectionEstimateConfigFromOnView(
      values.generalStandardAttachments
    ),
    sectionDescriptions: values.sectionDescriptions,
    sectionAttachments: readSectionEstimateConfigFromOnView(
      values.sectionAttachments
    ),
    controlledElementStandard: values.controlledElementStandard,
    controlledElementStandardAttachments: readSectionEstimateConfigFromOnView(
      values.controlledElementStandardAttachments
    ),
    controlledElementAttachments: readSectionEstimateConfigFromOnView(
      values.controlledElementAttachments
    ),
    showNotApplicable: values.showNotApplicable,
    showUnchecked: values.showUnchecked,
    issueAttachments: {
      enabled: values.issueAttachments.enabled,
      scope: values.issueAttachments.scope._id,
      displayStyle: values.issueAttachments.displayStyle._id,
    },
    fieldsContent: values.fieldsContent._id,
    selectedFields: values.selectedFields.map((field) => field._id).sort(),
    showFileNames: values.showFileNames,
    showFileDescriptions: values.showFileDescriptions,
    showActiveLinks: values.showActiveLinks,
    showPhoneNumbers: values.showPhoneNumbers,
    showUserEmails: values.showUserEmails,
    linkToHustro: values.linkToHustro,
    reportName: values.name,
    reportMessage: values.message.trim(),
    reportMessageInPdf: values.messagePresentation.inPdfReport,
  });
}

export function buildInspectionPageEstimateInput(
  values: SingleInspectionReportOnView,
  source: InspectionPageEstimateSource,
  getAvailableFieldsForProcess: (processId: string) => Set<IssueFieldNames>
): InspectionPageEstimateInput {
  const ia = values.issueAttachments;
  const templateData = deriveInspectionTemplateDataFromForm(values);

  return {
    parts: deriveInspectionReportPartsFromForm(values),
    templateData: {
      generalStandard: templateData.generalStandard,
      generalStandardAttachments: templateData.generalStandardAttachments,
      sectionDescriptions: templateData.sectionDescriptions,
      sectionAttachments: templateData.sectionAttachments,
      controlledElementStandard: templateData.controlledElementStandard,
      controlledElementStandardAttachments:
        templateData.controlledElementStandardAttachments,
    },
    inspectionData: {
      controlledElementAttachments: readSectionEstimateConfigFromOnView(
        values.controlledElementAttachments
      ),
      showNotApplicable: values.showNotApplicable,
      showUnchecked: values.showUnchecked,
    },
    issueAttachments: {
      enabled: ia.enabled,
      scope: ia.scope._id,
      displayStyle: readSectionEstimateConfigFromOnView(ia).displayStyle,
    },
    fieldsContent: isReportFieldsContent(values.fieldsContent._id)
      ? values.fieldsContent._id
      : ReportFieldsContent.filled,
    selectedFieldNames: values.selectedFields
      .map((field) => field._id)
      .filter(isIssueFieldName),
    getAvailableFieldsForProcess,
    fileMetadata: {
      showFileNames: values.showFileNames,
      showFileDescriptions: values.showFileDescriptions,
      showActiveLinks: values.showActiveLinks,
      showPhoneNumbers: values.showPhoneNumbers,
    },
    showUserEmails: values.showUserEmails,
    linkToHustro: deriveLinkToHustroFromForm(values),
    reportName: values.name.trim(),
    reportMessage: values.message.trim(),
    reportMessageInPdf: values.messagePresentation.inPdfReport,
    source,
  };
}
